// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $Model,
  $ModelUpsertParams,
} from 'common.data';

// Global Enums
import {
  adminPages,
  adminSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
  TdDate,
  TdImage,
} from '@vroskus/library-components';

// Helpers
import _ from 'lodash';
import {
  cleanFormValues,
  getImageUrl,
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
} from '../../../helpers';

// Components
import Page from '../../layout/Page';
import EmptyTable from '../../common/EmptyTable';
import EntryFields from '../../fields/EntryFields';
import ThQty from '../../common/ThQty';
import ThSort from '../../common/ThSort';

// Helpers
import config from '../../../config';

// Services
import Services from '../../../services';

// Types
import type {
  $ReducerState,
} from '../../../redux';
import type {
  $RedirectAction,
} from '../../../redux/modules/main/actions';

export type $OwnProps = $Translate;

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = {
  redirect: $RedirectAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$Model<'Entry'>>;
  dataSortConfig: null | string;
  loading: boolean;
};

const pageId = adminPages.adminEntriesPage;

class AdminEntriesPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      dataSortConfig: null,
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      main,
    } = this.props;

    if (main.activeRegionId !== null && isDiff(
      main.activeRegionId,
      prevProps.main.activeRegionId,
    )) {
      this.getData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          main,
        } = this.props;

        if (main.activeRegionId === data.RegionId && data.modelName === 'Entry') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      main,
    } = this.props;

    const RegionId = main.activeRegionId;

    await defaultGetData({
      action: async () => {
        if (RegionId !== null) {
          await this.setStateAsync({
            loading: true,
          });

          const unsortedData = await Services.Api.getManyAdminEntry({
            RegionId,
          });

          const data = _.orderBy(
            unsortedData,
            ['RegistrationNo'],
            ['asc'],
          );

          await this.setStateAsync({
            data,
            loading: false,
          });
        }
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  async sortData(newDataSortConfig: string): Promise<void> {
    const {
      data,
    } = this.state;

    const [key, direction] = newDataSortConfig.split(':');

    await this.setStateAsync({
      loading: true,
    });

    const sortedData = _.orderBy(
      data,
      [key],
      [direction === 'desc' ? 'desc' : 'asc'],
    );

    await this.setStateAsync({
      data: sortedData,
      dataSortConfig: newDataSortConfig,
      loading: false,
    });
  }

  renderHeader() {
    const {
      main,
      redirect,
      t,
    } = this.props;

    if (main.activeRegionId !== null) {
      const RegionId = main.activeRegionId;

      const newItem: $ModelUpsertParams<'Entry'> = {
        Club: null,
        Harbor: null,
        Image: null,
        Model: null,
        Name: null,
        RegionId,
        RegistrationNo: '',
        SailNo: null,
      };

      return (
        <Card id={adminSections[pageId].header}>
          <SectionHeader
            rightButtons={(
              <>
                <Button
                  className={'mr-1'}
                  color={'secondary'}
                  id={'redirect-admin-entries-unmatched-page-button'}
                  onClick={() => redirect({
                    pathname: '/admin/entries_unmatched',
                  })}
                  size={'sm'}
                >
                  {t('Unmatched entries')}
                </Button>
                <FormDialog
                  fields={(
                    <EntryFields item={newItem} />
                  )}
                  id={'create-entry-dialog'}
                  labelCloseButton={t('Close')}
                  labelSaveButton={t('Save')}
                  onAction={(values) => defaultUpsertItem({
                    action: async () => {
                      const params = cleanFormValues<$Model<'Entry'>>({
                        ...newItem,
                        ...values,
                      });

                      await Services.Api.upsertAdminEntry(params);
                    },
                  })}
                  subTitle={t('New')}
                  title={t('Entry')}
                >
                  <Button
                    color={'info'}
                    id={'create-entry-button'}
                    size={'sm'}
                    title={t('Add new entry')}
                  >
                    {t('Add new')}
                  </Button>
                </FormDialog>
              </>
            )}
            title={t('Entries')}
          />
        </Card>
      );
    }

    return null;
  }

  renderButtons(dataItem: $Model<'Entry'>) {
    const {
      redirect,
      t,
    } = this.props;

    const {
      id,
    } = dataItem;

    return (
      <div className={'d-flex justify-content-end'}>
        <Button
          className={'mr-1'}
          color={'secondary'}
          id={`redirect-admin-entry-crewmembers-page-button-${id}`}
          onClick={() => redirect({
            pathname: `/admin/entry/${id}/crewmembers`,
          })}
          size={'sm'}
        >
          {t('Crewmembers')}
        </Button>
        <Button
          className={'mr-1'}
          color={'secondary'}
          id={`redirect-admin-entry-ratings-page-button-${id}`}
          onClick={() => redirect({
            pathname: `/admin/entry/${id}/ratings`,
          })}
          size={'sm'}
        >
          {t('Ratings')}
        </Button>
        <FormDialog
          fields={(
            <EntryFields item={dataItem} />
          )}
          id={`update-entry-dialog-${dataItem.id}`}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$Model<'Entry'>>({
                ...dataItem,
                ...values,
              });

              await Services.Api.upsertAdminEntry(params);
            },
          })}
          subTitle={`${dataItem.RegistrationNo} ${dataItem.Name || ''}`}
          title={t('Entry')}
        >
          <Button
            className={'mr-1'}
            color={'warning'}
            id={`update-entry-button-${dataItem.id}`}
            outline
            size={'sm'}
          >
            {t('Edit')}
          </Button>
        </FormDialog>
        <DeleteDialog
          content={t('Do you really want to delete?')}
          id={`delete-entry-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => Services.Api.deleteAdminEntry(dataItem),
          })}
          subTitle={`${dataItem.RegistrationNo} ${dataItem.Name || ''}`}
          title={t('Entry')}
        >
          <Button
            color={'danger'}
            id={`delete-entry-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Delete')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
      dataSortConfig,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable className={'mt-5'} />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th>{t('Image')}</th>
            <ThSort
              dataSortConfig={dataSortConfig}
              label={t('Reg no.')}
              onSort={(newDataSortConfig: string) => this.sortData(newDataSortConfig)}
              sortKey={'RegistrationNo'}
            />
            <ThSort
              dataSortConfig={dataSortConfig}
              label={t('Title')}
              onSort={(newDataSortConfig: string) => this.sortData(newDataSortConfig)}
              sortKey={'Name'}
            />
            <ThSort
              dataSortConfig={dataSortConfig}
              label={t('Make / Model')}
              onSort={(newDataSortConfig: string) => this.sortData(newDataSortConfig)}
              sortKey={'Model'}
            />
            <ThSort
              dataSortConfig={dataSortConfig}
              label={t('Date created')}
              onSort={(newDataSortConfig: string) => this.sortData(newDataSortConfig)}
              sortKey={'createdAt'}
            />
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $Model<'Entry'>) => (
            <tr key={dataItem.id}>
              <TdImage
                largeImageUrl={getImageUrl({
                  cdnUrl: config.CDN_URL,
                  image: dataItem.Image,
                  size: 'max',
                })}
                noImageUrl={getImageUrl({
                  cdnUrl: config.CDN_URL,
                  image: null,
                  size: 'min',
                })}
                smallImageUrl={getImageUrl({
                  cdnUrl: config.CDN_URL,
                  image: dataItem.Image,
                  size: 'min',
                })}
                value={dataItem.Image}
              />
              <td>{dataItem.RegistrationNo}</td>
              <td>{dataItem.Name}</td>
              <td>{dataItem.Model}</td>
              <TdDate
                duration
                multiple
                value={dataItem.createdAt}
              />
              <td>
                {this.renderButtons(dataItem)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Card id={adminSections[pageId].list}>
        <Loader
          loading={loading === true}
          overlay
        >
          {this.renderTable()}
        </Loader>
      </Card>
    );
  }

  render() {
    return (
      <Page id={pageId}>
        <>
          {this.renderHeader()}
          {this.renderContent()}
        </>
      </Page>
    );
  }
}

export default AdminEntriesPageView;
